// src/page/SpotNew.js
import React, { useEffect, useRef, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { GENRE_OPTIONS } from '../constants/spotOptions';
import { numOrNull, toISO } from '../utils/formHelper';

function SpotNew() {
  useAuthGuard();
  const navigate = useNavigate();

  // ---------------- Messages ----------------
  const MSG = {
    apiError: 'サーバーエラーが発生しました',
    saved: '登録しました。',
    errSpotName: 'スポット名は必須です',
    errPublishFlag: '公開設定を選択してください',
    errPublicRangeReq: '公開期間は必須です（開始・終了を選択してください）',
    errPublicRangeFmt: '公開期間の形式が正しくありません（YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm）',
    errPublicOrder: '公開開始は公開終了以前である必要があります',
    errDisplayOrder: '表示順は数値で入力してください',
    errGenre: 'ジャンルは1つ以上選択してください',
    errActivityPoint: '行動ポイントは数値で入力してください',
    errLatitude: '緯度は-90～90の範囲で入力してください',
    errLongitude: '経度は-180～180の範囲で入力してください',
    uploadError: '画像のアップロードに失敗しました。'
  };

  // ---------------- Form states ----------------
  const [spotName, setSpotName] = useState('');
  const [spotDescription, setSpotDescription] = useState('');
  const [displayOrder, setDisplayOrder] = useState('');
  const [genre, setGenre] = useState([]); // CSV (array in UI)
  const [publishedFlag, setPublishedFlag] = useState('0'); // '0' | '1'
  const [publicRange, setPublicRange] = useState(''); // "YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
  const [spotLink, setSpotLink] = useState('');
  const [linkOpenMode, setLinkOpenMode] = useState('1');
  const [activityPoint, setActivityPoint] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');

  // Image (single) — same UX as SpotEdit
  const [imageFile, setImageFile] = useState(null);      // File | null
  const [imagePreview, setImagePreview] = useState('');  // blob URL
  const [isDragOver, setIsDragOver] = useState(false);

  // UI states
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [errors, setErrors] = useState({});

  // ---------------- Helpers ----------------
  const pad = (n) => String(n).padStart(2, '0');
  const RANGE_RE = /^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2} ~ \d{4}\/\d{2}\/\d{2} \d{2}:\d{2}$/;

  const parseDT = (s) => {
    const [d, t] = s.trim().split(' ');
    if (!d || !t) return null;
    const [Y, M, D] = d.split('/').map(Number);
    const [h, m] = t.split(':').map(Number);
    const x = new Date(Y, M - 1, D, h, m);
    return Number.isNaN(x.getTime()) ? null : x;
  };

  const invalid = (k) => (errors[k] ? 'is-invalid' : '');
  const feedback = (k) => (errors[k] ? <div className="invalid-feedback d-block">{errors[k]}</div> : null);

  // ---------------- flatpickr ----------------
  const fpRef = useRef(null);
  useEffect(() => {
    if (!window.flatpickr) return;
    fpRef.current = window.flatpickr('#public_range', {
      mode: 'range',
      enableTime: true,
      time_24hr: true,
      dateFormat: 'Y/m/d H:i',
      locale: window.flatpickr.l10ns?.ja || 'ja',
      onChange: (selectedDates) => { // [CHANGE] 変更/クリアを即時反映
        const f = (d) =>
          `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
        if (selectedDates.length === 2) {
          const [s, e] = selectedDates;
          setPublicRange(`${f(s)} ~ ${f(e)}`);
        } else if (selectedDates.length === 0) {
          setPublicRange('');
        }
      },
    });
    return () => { try { fpRef.current?.destroy(); } catch { } };
  }, []);

  useEffect(() => {
    if (!fpRef.current) return;
    if (!publicRange) { try { fpRef.current.clear(); } catch { } return; }
    if (publicRange.includes('~')) {
      const [a, b] = publicRange.split('~').map(s => s.trim());
      fpRef.current.setDate([a, b], true);
    }
  }, [publicRange]);

  const clearPublic = () => {
    setPublicRange('');
    try { fpRef.current?.clear(); } catch { }
  };

  // ---------------- Image helpers (like SpotEdit) ----------------
  const fileInputRef = useRef(null);
  const isBlobUrl = (u) => { try { return new URL(u).protocol === 'blob:'; } catch { return false; } };

  const setSingleImage = (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      alert('画像ファイルを選択してください');
      return;
    }
    if (imagePreview && isBlobUrl(imagePreview)) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handlePickClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setSingleImage(file);
    e.target.value = ''; // keep only one file
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) setSingleImage(file);
  };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); };

  const clearImage = () => {
    if (imagePreview && isBlobUrl(imagePreview)) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview('');
  };

  useEffect(() => {
    return () => {
      if (imagePreview && isBlobUrl(imagePreview)) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  // ---------------- Validation ----------------
  const validate = () => {
    const err = {};

    if (!spotName.trim()) err.spotName = MSG.errSpotName;

    if (publishedFlag !== '0' && publishedFlag !== '1') {
      err.publishedFlag = MSG.errPublishFlag;
    }
    if (!publicRange || publicRange.trim() === '') {
      err.publicRange = MSG.errPublicRangeReq;
    } else if (!RANGE_RE.test(publicRange.trim())) {
      err.publicRange = MSG.errPublicRangeFmt;
    } else {
      const [a, b] = publicRange.split('~').map(s => s.trim());
      const s = parseDT(a), e = parseDT(b);
      if (!s || !e) err.publicRange = MSG.errPublicRangeFmt;
      else if (s > e) err.publicRange = MSG.errPublicOrder;
    }
    if (displayOrder !== '' && Number.isNaN(Number(displayOrder))) {
      err.displayOrder = MSG.errDisplayOrder;
    }
    if (activityPoint !== '' && Number.isNaN(Number(activityPoint))) {
      err.activityPoint = MSG.errActivityPoint;
    }
    if (latitude !== '' && (Number.isNaN(Number(latitude)) || Number(latitude) < -90 || Number(latitude) > 90)) {
      err.latitude = MSG.errLatitude;
    }
    if (longitude !== '' && (Number.isNaN(Number(longitude)) || Number(longitude) < -180 || Number(longitude) > 180)) {
      err.longitude = MSG.errLongitude;
    }

    if (!Array.isArray(genre) || genre.length < 1) {
      err.genre = MSG.errGenre;
    }

    setErrors(err);
    return err;
  };

  // ---------------- Upload (after creation) ----------------
  const uploadImageForSpot = async (spotId) => {
    if (!imageFile) return null;
    setUploading(true);
    try {
      const token = localStorage.getItem('access_token');
      const fd = new FormData();
      fd.append('file', imageFile, imageFile.name);
      fd.append('path', `spot/${spotId}/`);
      const res = await fetch(`${API_BASE_URL}/v1/file`, {
        method: 'POST',
        headers: { Authorization: token ? `Bearer ${token}` : '' },
        body: fd
      });
      if (!res.ok) {
        let msg = `画像アップロードに失敗しました (HTTP ${res.status})`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
        throw new Error(msg);
      }
      let resultPath = null;
      try {
        const j = await res.json();
        resultPath = j?.path || j?.result || null;
      } catch {
        resultPath = await res.text();
      }
      return resultPath;
    } finally {
      setUploading(false);
    }
  };

  // ---------------- Submit ----------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    const err = validate();
    const keys = Object.keys(err);
    if (keys.length) {
      const first = keys[0];
      const node =
        document.querySelector(`[data-error-key="${first}"]`) ||
        document.querySelector(`[name="${first}"]`);
      if (node?.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const [lhs, rhs] = publicRange.split('~').map(s => s.trim());
    const payload = {
      spot: {
        spotName,
        spotDescription,
        displayOrder: numOrNull(displayOrder, true),
        spotGenre: (genre || []).join(','),
        publishedFlag: publishedFlag === '1',
        publishedStartDate: toISO(lhs),
        publishedEndDate: toISO(rhs),
        spotLink,
        launcherMode: linkOpenMode,
        activityPoint: numOrNull(activityPoint, true),
        latitude: latitude !== '' ? parseFloat(latitude) : null,
        longitude: longitude !== '' ? parseFloat(longitude) : null
        // spotImage will be set after upload
      }
    };

    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');

      // 1) Create spot (get ID)
      const res = await fetch(`${API_BASE_URL}/v1/spot`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        let msg = `作成に失敗しました (HTTP ${res.status})`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
        throw new Error(msg);
      }
      const j = await res.json().catch(() => ({}));
      const createdId = j?.spot?.spotId || j?.spotId;
      if (!createdId) {
        // proceed without image if ID is missing
        alert(MSG.saved);
        navigate('/spot');
        return;
      }

      // 2) If image selected, upload then update spotImage
      if (imageFile) {
        const uploadedPath = await uploadImageForSpot(createdId);
        if (!uploadedPath) throw new Error(MSG.uploadError);

        const putPayload = { spot: { spotImage: uploadedPath } };
        const putRes = await fetch(`${API_BASE_URL}/v1/spot/${encodeURIComponent(createdId)}`, {
          method: 'PUT',
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(putPayload)
        });
        if (!putRes.ok) {
          let msg = `画像パス保存に失敗しました (HTTP ${putRes.status})`;
          try { const jj = await putRes.json(); if (jj?.error) msg = jj.error; } catch { }
          throw new Error(msg);
        }
      }

      alert(MSG.saved);
      navigate('/spot');
    } catch (e2) {
      console.error(e2);
      setApiError(MSG.apiError);
      alert(MSG.apiError);
    } finally {
      setSaving(false);
    }
  };

  const savingOrUploading = saving || uploading;

  return (
    <Layout>
      <>
        <nav className="mb-3" aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item"><a href="#!">スポット管理</a></li>
            <li className="breadcrumb-item active">新規登録</li>
          </ol>
        </nav>

        {apiError && <div className="alert alert-danger">{apiError}</div>}

        <form className="mb-9" onSubmit={handleSubmit}>
          <div className="row g-3 flex-between-end mb-5">
            <div className="col-auto">
              <h2 className="mb-2">スポット新規登録</h2>
            </div>
            <div className="col-auto">
              <button
                className="btn btn-phoenix-secondary me-2 mb-2 mb-sm-0"
                type="button"
                onClick={() => window.history.back()}
                disabled={savingOrUploading}
              >キャンセル</button>
              <button
                className="btn btn-primary me-2 mb-2 mb-sm-0"
                type="submit"
                disabled={savingOrUploading}
              >{saving ? '登録中...' : (uploading ? '画像アップロード中...' : '登録')}</button>
            </div>
          </div>

          <div className="row g-5">
            <div className="col-12 col-xl-8">

              <div className={`mb-4 ${invalid('spotName')}`} data-error-key="spotName">
                <h4 className="mb-3">スポット名<span className="text-danger">*</span></h4>
                <input
                  className={`form-control ${invalid('spotName')}`}
                  type="text"
                  name="spotName"
                  value={spotName}
                  onChange={(e) => setSpotName(e.target.value)}
                  onInvalid={(e) => e.target.setCustomValidity('このフィールドを入力してください。')}
                  onInput={(e) => e.target.setCustomValidity('')}
                  disabled={savingOrUploading}
                />
                {feedback('spotName')}
              </div>
              <div className="mb-4">
                <h4 className="mb-3">内容</h4>
                <textarea
                  className="form-control"
                  name="spotDescription"
                  rows={4}
                  value={spotDescription}
                  onChange={(e) => setSpotDescription(e.target.value)}
                  disabled={savingOrUploading}
                />
              </div>
              <div className="mb-4">
                <h4 className="mb-3">関連リンク</h4>
                <div
                  className="input-group"
                  style={{ '--phoenix-btn-border-color': 'var(--phoenix-input-border-color)' }}
                >
                  <input
                    className="form-control"
                    type="url"
                    name="spotLink"
                    value={spotLink}
                    onChange={(e) => setSpotLink(e.target.value)}
                    disabled={savingOrUploading}
                    placeholder="https://example.com/..."
                  />
                  <button
                    type="button"
                    className={`btn ${linkOpenMode === '1' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setLinkOpenMode('1')}
                    disabled={savingOrUploading}
                    aria-pressed={linkOpenMode === '1'}
                  >
                    WebView
                  </button>
                  <button
                    type="button"
                    className={`btn ${linkOpenMode === '2' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setLinkOpenMode('2')}
                    disabled={savingOrUploading}
                    aria-pressed={linkOpenMode === '2'}
                  >
                    ブラウザ
                  </button>
                </div>
              </div>


            </div>

            <div className="col-12 col-xl-4">
              <div className="row g-2">
                <div className="col-12 col-xl-12">
                  <div className="card mb-3" data-error-key="publishedFlag">
                    <div className="card-body">
                      <div className={`mb-4 ${invalid('publishedFlag')}`}>
                        <h5 className="mb-3">公開設定<span className="text-danger">*</span></h5>
                        <div className="d-flex align-items-center gap-3">
                          <label className="mb-0">
                            <input
                              type="radio"
                              name="publish_status"
                              value="0"
                              checked={publishedFlag === '0'}
                              onChange={(e) => setPublishedFlag(e.target.value)}
                              required
                              disabled={savingOrUploading}
                            /> 非公開
                          </label>
                          <label className="mb-0">
                            <input
                              type="radio"
                              name="publish_status"
                              value="1"
                              checked={publishedFlag === '1'}
                              onChange={(e) => setPublishedFlag(e.target.value)}
                              required
                              disabled={savingOrUploading}
                            /> 公開
                          </label>
                        </div>
                        {feedback('publishedFlag')}
                      </div>

                      <div className="mb-4" data-error-key="publicRange">
                        <div className="d-flex flex-wrap mb-2">
                          <h5 className="mb-0 text-body-highlight me-2">
                            公開期間<span className="text-danger">*</span>
                          </h5>
                        </div>
                        <div className="input-group">
                          <input
                            className={`form-control ${invalid('publicRange')}`}
                            type="text"
                            name="public_range"
                            id="public_range"
                            placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                            value={publicRange}
                            required
                            pattern="^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2} ~ \d{4}\/\d{2}\/\d{2} \d{2}:\d{2}$"
                            onInvalid={(e) => {
                              const v = e.target.validity;
                              e.target.setCustomValidity(
                                v.valueMissing ? MSG.errPublicRangeReq : MSG.errPublicRangeFmt
                              );
                            }}
                            onInput={(e) => e.target.setCustomValidity('')}
                            readOnly
                            disabled={savingOrUploading}
                          />
                          <span
                            className="input-group-text"
                            role="button"
                            tabIndex={0}
                            onClick={clearPublic}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clearPublic(); }}
                            aria-label="クリア"
                            title="クリア"
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                          >×</span>
                        </div>
                        {feedback('publicRange')}
                      </div>
                      <div className="mb-4" data-error-key="displayOrder">
                        <div className="d-flex flex-wrap mb-2">
                          <h5 className="mb-0 text-body-highlight me-2">表示順</h5>
                        </div>
                        <input
                          className={`form-control ${invalid('displayOrder')}`}
                          type="number"
                          name="order"
                          value={displayOrder}
                          onChange={(e) => setDisplayOrder(e.target.value)}
                          disabled={savingOrUploading}
                        />
                        {feedback('displayOrder')}
                      </div>
                      <div className="mb-4" data-error-key="activityPoint">
                        <div className="d-flex flex-wrap mb-2">
                          <h5 className="mb-0 text-body-highlight me-2">行動ポイント</h5>
                        </div>
                        <input
                          className={`form-control ${invalid('activityPoint')}`}
                          type="number"
                          name="activityPoint"
                          value={activityPoint}
                          onChange={(e) => setActivityPoint(e.target.value)}
                          disabled={savingOrUploading}
                        />
                        {feedback('activityPoint')}
                      </div>
                      <div className="mb-4" data-error-key="latitude">
                        <div className="d-flex flex-wrap mb-2">
                          <h5 className="mb-0 text-body-highlight me-2">緯度</h5>
                        </div>
                        <input
                          className={`form-control ${invalid('latitude')}`}
                          type="number"
                          name="latitude"
                          step="0.00000001"
                          min="-90"
                          max="90"
                          value={latitude}
                          onChange={(e) => setLatitude(e.target.value)}
                          disabled={savingOrUploading}
                          placeholder="例: 37.91234567"
                        />
                        {feedback('latitude')}
                      </div>
                      <div className="mb-4" data-error-key="longitude">
                        <div className="d-flex flex-wrap mb-2">
                          <h5 className="mb-0 text-body-highlight me-2">経度</h5>
                        </div>
                        <input
                          className={`form-control ${invalid('longitude')}`}
                          type="number"
                          name="longitude"
                          step="0.00000001"
                          min="-180"
                          max="180"
                          value={longitude}
                          onChange={(e) => setLongitude(e.target.value)}
                          disabled={savingOrUploading}
                          placeholder="例: 139.02345678"
                        />
                        {feedback('longitude')}
                      </div>

                    </div>
                  </div>
                </div>

                {/* タグ系 */}
                <div className="col-12 col-xl-12" data-error-key="genre">
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className={`mb-4 ${invalid('genre')}`}>
                        <div className="d-flex flex-wrap mb-2">
                          <h5 className="mb-0 text-body-highlight me-2">
                            ジャンル<span className="text-danger">*</span>
                          </h5>
                        </div>
                        {GENRE_OPTIONS.map(([val, label]) => (
                          <label key={val} className="me-3">
                            <input
                              type="checkbox"
                              checked={genre.includes(val)}
                              onChange={(e) =>
                                setGenre(g => e.target.checked ? [...new Set([...g, val])] : g.filter(v => v !== val))
                              }
                              disabled={savingOrUploading}
                            /> {label}
                          </label>
                        ))}
                        {feedback('genre')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Spot image (single) — same as SpotEdit */}
                <div className="col-12 col-xl-12">
                  <div className="card mb-3">
                    <div className="card-body">
                      <h4 className="card-title mb-4">スポット画像</h4>

                      {!imagePreview && (
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={handlePickClick}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePickClick(); }}
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                          onDragEnter={handleDragOver}
                          onDragLeave={handleDragLeave}
                          className={`border border-dashed rounded-3 p-4 text-center ${isDragOver ? 'bg-body-tertiary' : ''}`}
                          style={{ cursor: 'pointer' }}
                          aria-label="画像をドラッグ&ドロップ、またはクリックして選択"
                        >
                          <p className="mb-2">ここに画像をドラッグ＆ドロップ</p>
                          <p className="mb-2">または</p>
                          <button type="button" className="btn btn-outline-primary" disabled={savingOrUploading}>ファイルを選択</button>
                          <p className="text-body-tertiary mt-2 mb-0">対応形式: 画像ファイル（1枚まで）</p>
                        </div>
                      )}

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        disabled={savingOrUploading}
                      />

                      {imagePreview && (
                        <div className="d-flex align-items-start gap-3">
                          <img
                            src={imagePreview}
                            alt="プレビュー"
                            className="rounded border"
                            style={{ width: 160, height: 160, objectFit: 'cover' }}
                          />
                          <div className="flex-grow-1">
                            <div className="mb-2 small text-body-secondary">
                              {imageFile?.name} {imageFile ? `(${Math.round(imageFile.size / 1024)} KB)` : ''}
                            </div>
                            <div className="d-flex gap-2">
                              <button type="button" className="btn btn-outline-secondary" onClick={handlePickClick} disabled={savingOrUploading}>
                                画像を変更
                              </button>
                              <button type="button" className="btn btn-outline-danger" onClick={clearImage} disabled={savingOrUploading}>
                                画像を削除
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {apiError && <div className="text-danger mt-2">{apiError}</div>}
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </form>
      </>
    </Layout>
  );
}

export default SpotNew;
